import moment from 'moment';
import React, { useEffect, useState } from 'react';
import {
  Banner,
  Box,
  Button,
  ContentBox,
  DateRangeInput,
  Label,
  LoadingOverlay,
  Select,
  SortTable,
  TeaserBox,
  Text
} from '@looxr/components';
import { SORT_DESC, useLanguage, useTranslation } from '@looxr/utils';
import { getReportExportLeakTableConfig } from '../../constants';
import { useLoadCustomers, useNumberFormat } from '../../hooks';
import { AppStateService, FirebaseService, LeakCalcService } from '../../services';

function LeakReport() {
  const { formatNumber } = useNumberFormat();
  const tn = useTranslation();
  const language = useLanguage();

  const { getCustomers } = useLoadCustomers();

  const [loading, setLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(AppStateService.isLOOXR());

  const [leaks, setLeaks] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [customers, setCustomers] = useState([]);
  const [filterCustomer, setFilterCustomer] = useState('all');

  const [range, setRange] = useState({
    from: moment().startOf('month').toDate(),
    to: moment().endOf('month').toDate()
  });

  // load customers for LOOXR / OEM / Service Partner to operate on
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const customerDocs = await getCustomers();
      const sorted = customerDocs.sort((a, b) => {
        if (a.name1.toLowerCase() < b.name1.toLowerCase()) {
          return -1;
        }
        if (a.name1.toLowerCase() > b.name1.toLowerCase()) {
          return 1;
        }
        return 0;
      });
      setCustomers(sorted);
      setLoading(false);
    };

    load();
  }, []);

  // Update Leaks on Range or Customer Filter Change
  useEffect(() => {
    const init = async () => {
      if (customers.length > 0) {
        let filteredCustomer = [...customers];

        if (filterCustomer !== 'all') {
          filteredCustomer = filteredCustomer.filter((customer) => customer.id === filterCustomer);
        }

        if (range.from && range.to) {
          setLoading(true);
          const leakDocs = await FirebaseService.getLeaksByCustomersAndRange(
            filteredCustomer,
            range
          );

          // enrich leak docs with customer data
          const enrichedLeakDocs = leakDocs.map((leak) => {
            const customer = filteredCustomer.find((customer) => customer.id === leak.customerID);
            return {
              ...leak,
              customerData: customer
            };
          });

          setLeaks(enrichedLeakDocs);
          setFiltered(enrichedLeakDocs);
          setLoading(false);
        }
      }
    };

    init();
  }, [customers, range, filterCustomer]);

  const updateRange = (value) => {
    setRange(value);
  };

  const updateCustomerFilter = (option) => {
    setFilterCustomer(option);
  };

  const getCustomerOptions = () => {
    let options = [];

    options.push({ value: 'all', label: 'Alle' });

    const customerOptions = customers.map((customer) => {
      return {
        value: `${customer.id}`,
        label: `${customer.name1} ${customer.name2 ?? ''}`
      };
    });

    options = [...options, ...customerOptions];

    return options;
  };

  const getDatesBetween = (startDate, endDate) => {
    const dates = [];

    // Clone the start date to avoid modifying the original
    const currentDate = moment(startDate).startOf('day');
    const lastDate = moment(endDate).endOf('day');

    // Keep adding dates until we pass the end date
    while (currentDate.isSameOrBefore(lastDate)) {
      dates.push(moment(currentDate));
      currentDate.add(1, 'days');
    }

    return dates;
  };

  const exportLeaks = async () => {
    const csvData = [];
    const csvHeader = [
      'Kunde',
      'Kunde erfasst am'
      // Add additional headers as needed
    ];

    // Generate date columns for each day in the range
    if (range.from && range.to) {
      const dateRange = getDatesBetween(range.from, range.to);

      // Add a column for each date in the range
      dateRange.forEach((date) => {
        csvHeader.push(date.format('DD.MM.YYYY'));
      });

      csvHeader.push('Energiekosten € / Jahr');
      csvHeader.push('Emission C02 kg / Jahr');
      csvHeader.push('Energieverbrauch kWh / Jahr');

      // Add the header row to csvData
      csvData.push(csvHeader);

      let exportCustomers = [];
      if (filterCustomer !== 'all') {
        exportCustomers = customers.filter((customer) => customer.id === filterCustomer);
      } else {
        // fetch customers to export from loaded leaks
        const customerIDs = [...new Set(leaks.map((leak) => leak.customerID))];
        exportCustomers = customers.filter((customer) => customerIDs.includes(customer.id));
      }

      // for every customer, loop all days, find leaks that are created on that day count annd summarize the savings
      for (let i = 0; i < exportCustomers.length; i += 1) {
        const customer = exportCustomers[i];
        const customerLeaks = leaks.filter((leak) => leak.customerID === customer.id);
        const customerRow = [
          customer.name1,
          moment(customer.createdAt.toDate()).format('DD.MM.YYYY')
        ];
        let energyCostSum = 0;
        let emissionSum = 0;
        let energyConsumptionSum = 0;

        // Loop through each date in the range
        dateRange.forEach((date) => {
          const dateLeaks = customerLeaks.filter((leak) => {
            const leakDate = moment(leak.createdAt.toDate()).startOf('day');
            return leakDate.isSame(date, 'day');
          });

          customerRow.push(dateLeaks.length);

          dateLeaks.forEach((leak) => {
            const calcValues = LeakCalcService.calculateValues(leak, customer);

            if (
              calcValues.energyCosts !== undefined &&
              calcValues.energyCosts !== null &&
              !Number.isNaN(calcValues.energyCosts)
            ) {
              energyCostSum += calcValues.energyCosts;
            }

            if (
              calcValues.emissionPerYear !== undefined &&
              calcValues.emissionPerYear !== null &&
              !Number.isNaN(calcValues.emissionPerYear)
            ) {
              emissionSum += calcValues.emissionPerYear;
            }

            if (
              calcValues.energyConsumptionYear !== undefined &&
              calcValues.energyConsumptionYear !== null &&
              !Number.isNaN(calcValues.energyConsumptionYear)
            ) {
              energyConsumptionSum += calcValues.energyConsumptionYear;
            }
          });
        });

        customerRow.push(`"${energyCostSum.toFixed(2).replace('.', ',')}"`);
        customerRow.push(`"${emissionSum.toFixed(2).replace('.', ',')}"`);
        customerRow.push(`"${energyConsumptionSum.toFixed(2).replace('.', ',')}"`);

        // customerRow.push(`"${formatNumber(emissionSum, 2, true)}"`);

        csvData.push(customerRow);
      }

      // console.log(csvData);

      // Create CSV content
      let csvContent = '';
      csvData.forEach((row) => {
        csvContent += row.join(',') + '\r\n';
      });

      // Create a Blob with the CSV content
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

      // Create a download link and trigger the download
      const fileName = `leak-report-${moment().format('YYYY-MM-DD')}.csv`;
      if (navigator.msSaveBlob) {
        // For IE and Edge
        navigator.msSaveBlob(blob, fileName);
      } else {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  return (
    <>
      <Banner text={tn('admin.page.leakreport.headline')} />
      <Box paddingX={10} paddingY={10}>
        {showWarning && (
          <TeaserBox>
            <Text size="sm" bold color="purple">
              {tn('admin.page.leakreport.looxrwarning')}
            </Text>
          </TeaserBox>
        )}
      </Box>

      <Box paddingX={10} paddingY={10} minHeight="calc(100vh - 143px)">
        <ContentBox width={1024}>
          <Box display="flex" direction="row" width="100%">
            <Box display="flex" direction="row" alignItems="center" flexGrow={1}>
              <Box>
                <Text bold size="4xl" color="purple" align="center">
                  {filtered.length}
                </Text>
                <Text bold color="black2" size="sm">
                  {tn('general.leaks')}
                </Text>
              </Box>

              <Box marginLeft={5}>
                <Text bold size="4xl" color="purple" align="center">
                  {filterCustomer !== 'all' ? 1 : customers.length}
                </Text>
                <Text bold color="black2" size="sm">
                  {tn('admin.page.report.filter.customers')}
                </Text>
              </Box>
            </Box>

            <Box display="flex" marginTop={3} width={250} direction="column">
              <Label htmlFor="filter-report-range">{tn('admin.page.report.filter.range')}</Label>
              <DateRangeInput
                locale={language}
                name="filter-report-range"
                range={range}
                onChange={(update) => updateRange(update)}
              />
            </Box>

            <Box width={250} marginTop={3} marginLeft={3}>
              <Label htmlFor="filter-customer">{tn('general.customer')}</Label>
              <Select
                id="filter-customer"
                name="filter-customer"
                value={filterCustomer}
                options={getCustomerOptions()}
                onChange={(e) => updateCustomerFilter(e.value)}
              />
            </Box>

            <Box width={200} marginTop={10} marginLeft={3}>
              <Button
                onClick={exportLeaks}
                disabled={filtered.length === 0}
                text={tn('admin.page.leakreport.export')} // "Speichern"
                type="submit"
              />
            </Box>
          </Box>
        </ContentBox>
        <ContentBox title={tn('general.leaks')} marginTop={5} noPadding>
          <SortTable
            resetPageOnDataUpdate={true}
            tableKey="reportLeaks"
            data={filtered}
            hasPagination={true}
            pagerCondensed={false}
            hasRowHover={false}
            perPage={25}
            saveState={true}
            noDataText={tn('admin.page.report.table.noData')}
            dataName={tn('general.leaks')}
            columns={getReportExportLeakTableConfig()}
            defaulSortBy="id"
            defaultSortOrder={SORT_DESC}
          />
        </ContentBox>
      </Box>

      <LoadingOverlay isVisible={loading} />
    </>
  );
}

export default LeakReport;
